import React, { useState } from 'react';
const API_URL = '';

export default function PreorderModal({ product, onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const progressPercent = product.preorderGoal
    ? Math.min((product.preorders / product.preorderGoal) * 100, 100)
    : null;

  const progressColor = progressPercent >= 100 ? '#00c853' : '#ce0021';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name || !formData.email) {
      setError('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/preorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          productId: product.id,
          productName: product.name
        })
      });
      if (!response.ok) throw new Error('Failed to submit preorder');
      await response.json();
      alert("✅ Preorder submitted! We'll contact you when/if the sales goal is met.");
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Preorder</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {/* Product Info Row */}
        <div style={styles.productInfo}>
          {/* Product Image */}
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              style={styles.productImage}
              onError={e => e.target.style.display = 'none'}
            />
          )}
          <div style={styles.productDetails}>
            <p style={styles.productName}>{product.name}</p>
            <p style={styles.productPrice}>${(product.price / 100).toFixed(2)}</p>

            {/* Preorder Progress */}
            {product.preorderGoal && (
              <div style={styles.progressSection}>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${progressPercent}%`,
                      backgroundColor: progressColor
                    }}
                  />
                </div>
                <div style={styles.progressStats}>
                  <span style={{ color: progressColor, fontWeight: 'bold' }}>
                    {product.preorders} preorders
                  </span>
                  <span style={{ color: '#888' }}>
                    Goal: {product.preorderGoal} ({progressPercent.toFixed(0)}%)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Secured Checkout Badge */}
        <div style={styles.securedBadge}>
          <span style={styles.lockIcon}>🔒</span>
          <span>Secured Checkout — Your information is protected</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your Name *"
            style={styles.input}
            required
          />
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Your Email *"
            style={styles.input}
            required
          />
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Special requests or questions? (Optional)"
            style={styles.textarea}
            rows="3"
          />
          <div style={styles.notice}>
            <strong>⚠️ Important:</strong> Thank you! We'll contact you when/if the sales goal is reached.
          </div>
          <div style={styles.buttons}>
            <button type="button" onClick={onClose} style={styles.cancelBtn} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.confirmBtn,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting...' : 'Confirm Preorder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)'
  },
  modal: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '12px',
    maxWidth: '460px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(206,0,33,0.3), 0 0 0 1px rgba(206,0,33,0.2)',
    border: '1px solid rgba(206,0,33,0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid rgba(206,0,33,0.3)'
  },
  title: {
    margin: 0,
    fontSize: '22px',
    color: '#ce0021',
    fontWeight: 'bold'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: '#ce0021',
    padding: 0,
    width: '32px',
    height: '32px'
  },
  productInfo: {
    display: 'flex',
    gap: '1rem',
    padding: '15px 20px',
    borderBottom: '1px solid rgba(206,0,33,0.2)',
    alignItems: 'flex-start'
  },
  productImage: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid rgba(206,0,33,0.3)',
    flexShrink: 0
  },
  productDetails: {
    flex: 1
  },
  productName: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff'
  },
  productPrice: {
    margin: '0 0 10px 0',
    fontSize: '18px',
    color: '#ce0021',
    fontWeight: 'bold'
  },
  progressSection: {
    marginTop: '6px'
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '6px'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease'
  },
  progressStats: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.78rem'
  },
  securedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '8px 20px',
    background: 'rgba(0,200,83,0.08)',
    borderBottom: '1px solid rgba(0,200,83,0.2)',
    color: '#00c853',
    fontSize: '0.82rem',
    fontWeight: '600'
  },
  lockIcon: {
    fontSize: '1rem'
  },
  form: {
    padding: '20px'
  },
  error: {
    backgroundColor: 'rgba(206,0,33,0.2)',
    color: '#ff6b7a',
    padding: '10px',
    borderRadius: '6px',
    marginBottom: '15px',
    fontSize: '13px',
    border: '1px solid rgba(206,0,33,0.4)'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '1px solid rgba(206,0,33,0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box',
    marginBottom: '12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '1px solid rgba(206,0,33,0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box',
    marginBottom: '12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none'
  },
  notice: {
    backgroundColor: 'rgba(206,0,33,0.15)',
    border: '1px solid rgba(206,0,33,0.3)',
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '15px',
    fontSize: '12px',
    color: '#ffccd4',
    lineHeight: '1.4'
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  confirmBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg,#ce0021 0%,#a00019 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(206,0,33,0.4)',
    transition: 'all 0.3s'
  },
  cancelBtn: {
    padding: '12px 24px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s'
  }
};
